/**
 * Turns whatever someone typed into the "portal address" box into a usable
 * organization slug, or null when it cannot be one.
 *
 * The box is on the unknown-slug fallback (board 7f, right half), which is
 * reached precisely BECAUSE the address in the browser was wrong — so the input
 * has to survive the ways people restate a web address. All of these give
 * "starbucks": `starbucks`, `STARBUCKS`,
 * `starbucks.dev.customer.portal.davaco.cloud`, and
 * `https://starbucks.dev.customer.portal.davaco.cloud/dashboard`.
 *
 * The FIRST label is taken when the input still has dots, because a pasted
 * hostname carries the slug in front. That is deliberately not the rule
 * `resolveTenantContext` uses on a real hostname — it reads the LAST label
 * before the base host, so a deeper `preview.starbucks.<base>` still resolves
 * to "starbucks". Here there is no trustworthy base host to measure from: the
 * visitor may well paste the address of a DIFFERENT environment than the one
 * serving this page, and the front label is right in every case a person
 * actually types.
 *
 * Returning null rather than throwing keeps the caller a plain guard: an empty
 * or malformed box is not an error to report, it is a button that stays off.
 */
export function normalizePortalSlug(input: string): string | null {
  const withoutScheme: string = input.trim()
.toLowerCase()
.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");

  // Everything from the first path, query or fragment separator is address, not
  // slug. Splitting on the class rather than stripping each in turn means a
  // "slug/a?b#c" cannot smuggle one past the others.
  const host: string = withoutScheme.split(/[/?#]/)[0] ?? "";

  // A fully-qualified name may carry a root dot, and a typo may leave a
  // leading one; neither should reach the label test.
  const label: string = host.replace(/^\.+|\.+$/g, "")
.split(".")[0] ?? "";

  // A DNS label: alphanumeric ends, hyphens only inside, 63 characters at most.
  // The single-character case has no interior to match, hence the alternation.
  if (!/^[a-z0-9]$|^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/.test(label)) {
    return null;
  }

  // "www" is the apex host under another name — resolveTenantContext maps it
  // to the Davaco door, so accepting it here would send the visitor in a
  // circle back to this very screen.
  if (label === "www") {
    return null;
  }

  return label;
}
